import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/authService';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const campaignCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'Campaign',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.campaigns.`,
});

export async function getCampaigns(forceRefresh = false) {
    if (!forceRefresh) {
        const memCached = campaignCache.getFromMemory('all');
        if (memCached) return memCached;

        try {
            const legacyData = await iframeDataBridge.getStorageAsync('CAMPAIGNS');
            if (legacyData) {
                log('info', 'Campaign', '📦 Seeding Vue cache from legacy storage (CAMPAIGNS)');
                await campaignCache.put('all', legacyData);
                campaignCache.setInMemory('all', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'Campaign', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    try {
        const cached = await campaignCache.get('all');
        if (cached && !forceRefresh) {
            campaignCache.setInMemory('all', cached);

            if (!iframeDataBridge.isOnline()) {
                log('info', 'Campaign', '📴 Offline: Returning cached campaigns');
                return cached;
            }

            setTimeout(() => refreshCampaignsInBackground(), 100);
            return cached;
        }

        if (!iframeDataBridge.isOnline()) {
            log('warn', 'Campaign', '📴 Offline: No cached campaigns available');
            return [];
        }

        return await fetchCampaignsFromAPI();
    } catch (error) {
        log('error', 'Campaign', `❌ Error getting campaigns: ${error.message}`);

        try {
            const fallback = await campaignCache.get('all');
            if (fallback) {
                log('info', 'Campaign', '💾 Using fallback cached campaigns');
                return fallback;
            }
        } catch (cacheError) {
            log('error', 'Campaign', `❌ Cache fallback failed: ${cacheError.message}`);
        }

        return [];
    }
}

export async function getCampaignOfferPrecos(campaignIds) {
    try {
        if (!iframeDataBridge.isOnline()) {
            log('warn', 'Campaign', '📴 Offline: Cannot fetch campaign offer precos');
            return [];
        }

        const response = await authenticatedFetch(getApiUrl('Core/CampaignOfferPrecos'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const campaignOfferPrecos = await response.json();

        log('success', 'Campaign', `✅ Fetched ${campaignOfferPrecos.length} campaign offer precos`);
        return campaignOfferPrecos;
    } catch (error) {
        log('error', 'Campaign', `❌ Failed to fetch campaign offer precos from API: ${error.message}`);
        return [];
    }
}

async function fetchCampaignsFromAPI() {
    try {
        const response = await authenticatedFetch(getApiUrl('Core/Campaigns'), { method: 'GET' }, 'token');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const campaigns = await response.json();
        await campaignCache.put('all', campaigns);
        campaignCache.setInMemory('all', campaigns);

        log('success', 'Campaign', `✅ Fetched and cached ${campaigns.length} campaigns`);
        return campaigns;
    } catch (error) {
        log('error', 'Campaign', `❌ Failed to fetch campaigns from API: ${error.message}`);
        throw error;
    }
}

async function refreshCampaignsInBackground() {
    try {
        await fetchCampaignsFromAPI();
        log('success', 'Campaign', '🔄 Background refresh of campaigns completed');
    } catch (error) {
        log('warn', 'Campaign', `⚠️ Background refresh failed: ${error.message}`);
    }
}

export async function refreshCampaigns() {
    campaignCache.clearMemory();
    await campaignCache.clear();
}
